/**
 * 🐟 **The shape of a fish — T220, D115.**
 *
 * ## 🔴 Minecraft's answer, read off Mojang's own files, and we are taking it
 *
 * `Development/Minecraft/17_Fishing_And_Fish.md` §5 is unusually definite,
 * because it is **tier 1 — the published resource pack, not a wiki page**:
 *
 * > A cod is **three boxes and three flat planes**. There are no keyframes,
 * > no skeleton, no skinning, no bone weights and no artist-authored animation
 * > of any kind. A fish swims because three rigid parts, hanging off each other,
 * > are each rotated by the same cosine at a different amplitude, and the
 * > parent-child chain turns three independent wobbles into one travelling wave
 * > down the body.
 *
 * **The amplitudes, in full:** body **2°**, head **4°**, tail **−25.75°**.
 *
 * ✅ **That is the settler's own trick** — `legsplit.ts` swings a leg by a sine
 * of the distance walked with no skeleton — **pointed at a tail instead**. The
 * research found the same architecture for a different creature in a file Mojang
 * publishes, which **upgrades doc 16's finding from modding hearsay to fact.**
 *
 * ## 🎯 And the fish itself is a PHOTOGRAPH, not geometry
 *
 * 🎯 **haugur, 2026-09-16:** *"we should me using our API ai to design the
 * fish, as we usually do"* — said while this module was busy sculpting a cod out
 * of eight-sided prisms. ✅ **He was right**, and it made the shape question
 * answer itself: a photograph goes on a flat card, and the fish's outline comes
 * from the picture's own transparency.
 *
 * ⚠️ **Four sculpted candidates were baked for him and are now gone from this
 * file.** They are in `Design/samples/86_fish_shapes*.png` and in the git
 * history; keeping dead geometry beside the thing that replaced it is how a
 * reader learns the wrong lesson. **What survived is the WAVE**, which the card
 * needs just as much.
 *
 * ## ✅ So the wave is BAKED INTO THE VERTICES, and that is the whole design
 *
 * 🔴 **T218 is open: mesh building already stalls the player's frame.** So this
 * module emits, per vertex, **how far along the fish it sits** — 0 at the nose,
 * 1 at the tail tip — and the shader bends the fish from that. **Nothing is
 * rebuilt to make a fish swim.**
 *
 * ⚠️ **One number per vertex is the entire animation system.** No bones, no
 * hierarchy, no matrices per part. The travelling wave falls out of multiplying
 * the offset by how far along the body you are: the nose barely moves, the tail
 * sweeps, and the phase lag makes it travel.
 *
 * ## ⚠️ What this module deliberately does NOT do
 *
 * No `three` import, no material, no colour — it returns plain arrays, so it
 * tests as arithmetic without a browser or a GPU. **The same split
 * `driftgeom.ts` and `driftmesh.ts` already use**, and for the same reason: this
 * host has no GPU, so anything that can be arithmetic must be.
 */

/** What this module hands back. Plain arrays; the wrapper makes it a mesh. */
export interface FishShape {
  /** Triples, in METRES, relative to the fish's own nose-to-tail axis (+x is forward). */
  readonly positions: Float32Array;
  readonly normals: Float32Array;
  /**
   * 🔴 **One per vertex: how far along the fish this vertex sits, 0 at the nose
   * and 1 at the tail tip.** This is the animation, and there is nothing else.
   */
  readonly along: Float32Array;
  readonly uvs: Float32Array;
  readonly indices: Uint32Array;
  readonly vertexCount: number;
}

/**
 * 🐟 **The fish as a CARD — a flat rectangle carrying the photograph.**
 *
 * 🎯 **haugur, 2026-09-16:** *"we should me using our API ai to design the fish,
 * as we usually do"* — said while a session was busy sculpting a cod out of
 * eight-sided prisms by hand. 🔴 **He was right, and it settles the shape
 * question as a side effect:** a photograph belongs on a plane, and the fish's
 * outline comes from the picture's own transparency rather than from geometry.
 *
 * ✅ **This is what the game draws**, and the only shape in this file. The four
 * sculpted candidates it replaced are in `Design/samples/86_fish_shapes*.png`.
 *
 * ⚠️ **Subdivided along its length, and that is the whole reason it is not one
 * quad.** A card of two triangles cannot bend — the wave would slide it sideways
 * like a plank. Each cut gets its own `along`, so the shader can curve it.
 *
 * @param length nose to tail, in metres
 * @param aspect the picture's width ÷ height, so the card is never stretched
 * @param cuts how many divisions along the body. **Eight is plenty** — the wave
 *   is smooth and a fish is 60 px on screen at the range you meet one.
 * @param across how wide the FLAT card is, as a share of the upright one's
 *   height — see {@link FISH_ACROSS}.
 */
/**
 * 🐟 **How wide a fish is across, as a share of how deep it is** — T228.
 *
 * 🎯 **haugur, issue #664:** *"sometimes the still try to swim flat, especcially
 * when they a re fleeing, also the fish themselfs are very thin, like 2 sides of
 * paper, maybe their bodies just need a little bit more volume (fatter) so the
 * look more natual."*
 *
 * 🔴 **BOTH HALVES OF THAT ARE ONE FAULT, AND IT HAD A NUMBER.** The two crossed
 * cards used **the same half-extent**, so the flat one was exactly as wide as
 * the upright one is tall. 📏 **On an 80 cm þorskur that is 31 cm across. A real
 * Atlantic cod is about 9 to 10 cm.**
 *
 * ⚠️ **So from above — and any time a fish TURNED and swung that card through
 * your view — you saw a full-size fish silhouette lying flat like a sheet of
 * paper.** 🔴 **Worst while fleeing, because that is when they turn fastest**,
 * which is exactly what he reported.
 *
 * ✅ **0.32 — the real fish's width against the picture's full height**, fins
 * and tail spread included, because that is what the card's height actually is.
 *
 * ⚠️ **A DIAL FOR HIS EYE, not a constant to defend.** The measurement says
 * *about a third*; whether a third looks right through water is his to say.
 */
export const FISH_ACROSS = 0.32;

export function buildFishCard(
  length: number,
  aspect: number,
  cuts = 8,
  across = FISH_ACROSS,
): FishShape {
  const positions: number[] = [];
  const normals: number[] = [];
  const along: number[] = [];
  const uvs: number[] = [];
  const indices: number[] = [];
  const height = length / aspect;

  /**
   * 🔴 **TWO cards crossed at 90°, and a photograph of the game proved it has
   * to be two.**
   *
   * The first build was one upright card, and from the shore — **which is where
   * every fish in this game will be looked at** — it was **edge-on**: a thin
   * dark line in the water instead of a fish. ⚠️ **You look DOWN at a fish**, so
   * an upright card is the one orientation that hides it.
   *
   * 🔴 **It is candidate ② of the shapes haugur was shown this morning, and its
   * flaw was written down at the time** — *"it vanishes when you see it exactly
   * edge-on"* — **and then walked into anyway.** ✅ **Only baking a picture of
   * the real game caught it**, which is the fifth time in one day.
   *
   * ✅ **So: one upright, one flat.** The upright card carries the fish seen
   * from the side, the flat one carries it seen from above. **Both wear the same
   * photograph** — a þorskur's back and its flank are near enough at 80 cm
   * through water that a second picture would be a second thing to keep in step
   * for no gain. **It is exactly the trick `kjarr` uses** (T159).
   */
  const planes: readonly (readonly [number, number])[] = [
    [1, 0],
    [0, 1],
  ];

  for (const [uy, uz] of planes) {
    const base = positions.length / 3;
    for (let c = 0; c <= cuts; c++) {
      const t = c / cuts;
      /* ⚠️ The nose is at x = 0 and the tail at x = length, so `swimOffset`
         means the same thing for every vertex of both cards. */
      for (const side of [1, -1]) {
        /* 🔴 The upright card carries the fish's DEPTH; the flat one carries its
           WIDTH, and a fish is far narrower than it is deep. **They used to
           share one number** — see {@link FISH_ACROSS}. */
        const r = (side * height * (uz === 0 ? 1 : across)) / 2;
        positions.push(t * length, uy * r, uz * r);
        /* 🔴 ONE normal for every corner of both planes, and it points UP.
           `Minecraft/13` §1: each element carries `shade: false`, because
           without it two crossed planes take different directional shading and
           read as folded card rather than as one object. The crosses already
           do this and for the same reason. */
        normals.push(0, 1, 0);
        along.push(t);
        /**
         * 🔴 **THE FISH WERE UPSIDE DOWN, AND THIS ONE NUMBER IS WHY.**
         *
         * 🎯 **haugur, [#651](https://git.asgeir.is/haugur/Land-of-Fire-and-Ice/issues/651):**
         * *"aLL THE FISH ARE UPSIDE DOWN."* Every one of them swam belly-up.
         *
         * ⚠️ **`v = 0` is the BOTTOM of the picture, not the top.** Three's
         * textures start at the bottom-left and the loader flips the image in,
         * so **the fish's pale belly lives at `v = 0` and its dark back at
         * `v = 1`**. Putting `v = 0` on the upper edge of the card hung the
         * picture the wrong way up.
         *
         * 👉 **The side that points UP takes the top of the picture.** Said as a
         * sentence rather than as a number, because the number is the kind that
         * looks equally right either way round.
         */
        uvs.push(t, side === 1 ? 1 : 0);
      }
    }
    for (let c = 0; c < cuts; c++) {
      const i = base + c * 2;
      indices.push(i, i + 1, i + 3, i, i + 3, i + 2);
    }
  }

  return {
    positions: new Float32Array(positions),
    normals: new Float32Array(normals),
    along: new Float32Array(along),
    uvs: new Float32Array(uvs),
    indices: new Uint32Array(indices),
    vertexCount: positions.length / 3,
  };
}

/**
 * 🌊 **How far sideways a point on the fish is pushed, at this moment.**
 *
 * 🔴 **The whole swim, in one expression, and it is Minecraft's.** The research
 * gives body 2°, head 4°, tail −25.75° about their own pivots; a parent-child
 * chain of three rigid parts turns that into a travelling wave. **We have no
 * chain**, so the same wave is written directly: amplitude grows along the body,
 * and the phase lags behind it so the bend travels tail-ward.
 *
 * ⚠️ **Exported so a TEST can assert the wave rather than a shader being
 * believed**, and so the design tool draws exactly what the game will.
 *
 * @param along 0 at the nose, 1 at the tail tip
 * @param seconds the clock
 * @param phase this fish's own place in its beat, 0–1 — ⚠️ **per fish**, or a
 *   shoal beats as one object with fins
 * @param length the fish's length in metres, so the sweep scales with it
 */
export function swimOffset(
  along: number,
  seconds: number,
  phase: number,
  length: number,
  pace = 1,
): number {
  /**
   * Beats per second. A cod cruises; this is not a startled fish.
   *
   * 🔴 **Multiplied by the fish's OWN pace** — 🎯 haugur, #647: *"the all have
   * the same move."* ⚠️ **Every fish beat its tail at exactly 2.2 a second**, so
   * a shoal moved like one object with several parts. **A fish that swims
   * faster beats faster**, which is true of the real animal and free here.
   */
  const rate = 2.2 * pace;
  /* 🔴 The nose barely moves and the tail sweeps: squaring is what puts the
     motion at the back, and without it the whole fish slides sideways like a
     plank. */
  const amplitude = along * along * 0.17 * length;
  /* The lag down the body. Larger values make a tighter, more eel-like wave. */
  const lag = 1.4;
  return Math.sin((seconds * rate + phase) * Math.PI * 2 - along * lag) * amplitude;
}
